import React from 'react';
import styled from 'styled-components';

import type { DomainShareScope } from '../../../types/api';
import { SHARE_SCOPES } from '../lib/shareScopes';
import { ShareScopeOptionCard } from './ShareScopeOptionCard';

const Group = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
`;

export interface ShareScopeSelectorProps {
  value: DomainShareScope | null;
  counts: Partial<Record<DomainShareScope, number>>;
  onChange: (scope: DomainShareScope) => void;
}

/**
 * Radiogroup over the four domain scopes.
 *
 * Uses roving tabindex (one stop for the whole group, arrows to move) rather than
 * `aria-activedescendant`, matching the radio pattern the cards implement.
 */
export const ShareScopeSelector: React.FC<ShareScopeSelectorProps> = ({
  value,
  counts,
  onChange,
}) => {
  const groupRef = React.useRef<HTMLDivElement>(null);

  const selectableScopes = React.useMemo(
    () =>
      SHARE_SCOPES.filter((config) => counts[config.scope] !== 0).map(
        (config) => config.scope,
      ),
    [counts],
  );

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    const keys = [
      'ArrowDown',
      'ArrowRight',
      'ArrowUp',
      'ArrowLeft',
      'Home',
      'End',
    ];
    if (!keys.includes(event.key) || selectableScopes.length === 0) return;

    event.preventDefault();
    const currentIndex = value ? selectableScopes.indexOf(value) : -1;

    let nextIndex: number;
    if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = selectableScopes.length - 1;
    } else {
      const step =
        event.key === 'ArrowDown' || event.key === 'ArrowRight' ? 1 : -1;
      nextIndex =
        currentIndex === -1
          ? 0
          : (currentIndex + step + selectableScopes.length) %
            selectableScopes.length;
    }

    const nextScope = selectableScopes[nextIndex];
    if (!nextScope) return;

    onChange(nextScope);
    groupRef.current
      ?.querySelector<HTMLElement>(`[data-scope="${nextScope}"]`)
      ?.focus();
  };

  return (
    <Group
      ref={groupRef}
      role="radiogroup"
      aria-label="Alcance del paquete compartido"
      onKeyDown={handleKeyDown}
    >
      {SHARE_SCOPES.map((config) => (
        <ShareScopeOptionCard
          key={config.scope}
          config={config}
          selected={value === config.scope}
          fileCount={counts[config.scope]}
          onSelect={onChange}
        />
      ))}
    </Group>
  );
};
